import {blockRangeContains, lineRangeContains, rangeIntersect} from "./ranges.js";
import {onlyAllowed} from "./collections.js";
import {blank} from "./commands/trim.js";

export function isSingleCursor(state) {
  return state.selection.main.empty;
}

export function isCursorInInlineSpace(state) {
  if (isSingleCursor(state) && isInlineSpace(state)) {
    return !isInPhantomInlineCode(state);
  }
  return false;
}

function isInPhantomInlineCode(state) {
  const cursor = cursorAnchor(state);
  const surround = state.sliceDoc(cursor - 1, cursor + 1);
  if (surround === '``') {
    return !hasTypeBeforeCursor(state, 'Escape');
  }
  return false;
}

function cursorAnchor(state) {
  if (isSingleCursor(state)) {
    const {anchor} = state.selection.main;
    return anchor;
  }
  throw 'Use a different method to call types for selection';
}

export function hasTypeBeforeCursor(state, type) {
  const {from} = state.selection.main;
  return state.tree.topNode.resolve(from, -1).type.name === type;
}

export const tableTypes = ['Table', 'TableRow', 'TableCell'];
export const unformattedTypes = ['Paragraph', 'ListItem', 'BulletList', 'OrderedList', 'Escape'];

export function isInlineSpace(state) {
  return selectedTypesOnlyAllowed(state, [...unformattedTypes, ...tableTypes]);
}

export function selectedTypesOnlyAllowed(state, allowed) {
  return onlyAllowed(selectedTypes(state), allowed);
}

export function selectedTypesNoneOf(state, types) {
  return !setContainsAnyOf(selectedTypes(state), types);
}

function setContainsAnyOf(set, items) {
  return items.some(item => set.has(item));
}

export function selectedTypes(state) {
  return flatTypes(typesInRange(state, state.selection.main));
}

export function flatTypes({containing, contained, crossing, equal}) {
  return new Set([...crossing, ...contained, ...containing, ...equal]);
}

export function rangeHasType(state, {from, to}, type) {
  let has = false;
  state.tree.iterate({
    from, to, enter(_type) {
      if (_type.name === type) {
        has = true;
        return false;
      }
    }
  });
  return has;
}

export function typesInRange(state, range) {
  const containing = new Set();
  const contained = new Set();
  const crossing = new Set();
  const equal = new Set();
  state.tree.iterate({
    from: range.from,
    to: range.to,
    enter(type) {
      const {from, to} = type;
      if (type.name === 'Document') {
        return;
      }
      if (rangeEquals(range, {from, to})) {
        equal.add(type.name);
      } else if (rangeContainsByType(type.name, range, {from, to})) {
        containing.add(type.name);
      } else if (rangeContainsByType(type.name, {from, to}, range)) {
        contained.add(type.name);
      } else if (rangeIntersect({from, to}, range)) {
        crossing.add(type.name);
      }
    }
  });
  return {containing, contained, crossing, equal};
}

function rangeEquals(range, other) {
  return range.from === other.from && range.to === other.to;
}

function rangeContainsByType(type, range, other) {
  if (isBlockType(type)) {
    return blockRangeContains(range, other);
  }
  return lineRangeContains(range, other);
}

const blockElements = new Set(['CodeBlock', 'Paragraph', 'FencedCode', 'CodeText', 'CommentBlock', 'Blockquote']);

function isBlockType(type) {
  return blockElements.has(type);
}

export function selectedText(state) {
  const {from, to} = state.selection.main;
  return state.sliceDoc(from, to);
}

export function isSelectionBlank(state) {
  return blank(selectedText(state));
}

export function isFirstLine(state, from) {
  if (from === 0) {
    return true;
  }
  return state.doc.lineAt(from).from === 0;
}

export function previousLineRange(state, position) {
  const lineStart = state.doc.lineAt(position).from;
  return state.doc.lineAt(lineStart - 1);
}
